import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useAppDispatch, useAppSelector } from "../../../../Redux/hooks";
import { CreateAddressRequest } from "../../../../Redux/customer-address/types";
import {
  addAddress,
  clearAddressState,
} from "../../../../Redux/customer-address/slice";
import {
  fetchAllCountries,
  fetchStatesByCountryName,
  fetchDistrictsByStateName,
} from "../../../../Redux/location-mangment/slice";
import { AppDispatch, RootState } from "../../../../store";
import { toast } from "react-toastify";
import LocationPicker, { ResolvedAddress } from "../../LocationPicker";

// ── Reusable SearchDropdown ───────────────────────────────────────────────────
interface SearchDropdownProps {
  label: string;
  value: string;
  options: string[];
  onSelect: (v: string) => void;
  disabled?: boolean;
  loading?: boolean;
  placeholder?: string;
  onOpen?: () => void;
}

const SearchDropdown: React.FC<SearchDropdownProps> = ({
  label,
  value,
  options,
  onSelect,
  disabled = false,
  loading = false,
  placeholder = "Search...",
  onOpen,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  const filtered = options.filter((o) =>
    o.toLowerCase().includes(search.toLowerCase()),
  );

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="position-relative w-100" ref={ref}>
      <label className="form-label text-black fw-bold">{label}</label>
      <div
        className="input-group input-group-outline input-group-lg"
        style={{ cursor: disabled ? "not-allowed" : "pointer" }}
        onClick={() => {
          if (!disabled) {
            if (!open) onOpen?.();
            setOpen((p) => !p);
          }
        }}
      >
        <input
          type="text"
          className="form-control"
          placeholder={open ? placeholder : value || placeholder}
          value={open ? search : value}
          readOnly={!open}
          onChange={(e) => setSearch(e.target.value)}
          onClick={(e) => {
            e.stopPropagation();
            if (!disabled) {
              if (!open) onOpen?.();
              setOpen(true);
            }
          }}
          style={{
            cursor: disabled ? "not-allowed" : "text",
            background: disabled ? "#f8f9fa" : "",
          }}
        />
        <span className="input-group-text px-2">
          {loading ? (
            <span className="spinner-border spinner-border-sm" role="status" />
          ) : (
            <i className={`ri-arrow-${open ? "up" : "down"}-s-line fs-5`} />
          )}
        </span>
      </div>

      {open && (
        <div
          className="border border-radius-lg shadow-lg bg-white position-absolute w-100"
          style={{
            top: "100%",
            maxHeight: 200,
            overflowY: "auto",
            zIndex: 9999,
          }}
        >
          {loading ? (
            <div className="text-center py-3 text-sm text-muted">
              Loading...
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-3 text-sm text-muted">
              No results
            </div>
          ) : (
            filtered.map((opt) => (
              <div
                key={opt}
                className="dropdown-item px-3 py-2 cursor-pointer"
                style={{ fontSize: "0.875rem" }}
                onMouseDown={() => {
                  onSelect(opt);
                  setOpen(false);
                  setSearch("");
                }}
              >
                {opt}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};
// ─────────────────────────────────────────────────────────────────────────────

const NewAddressForm = () => {
  const dispatch = useAppDispatch();
  const locDispatch = useDispatch<AppDispatch>();

  const { success, message, error, addresses } = useAppSelector(
    (state) => state.customerAddress,
  );

  const {
    countries,
    states,
    districts,
    loading: locLoading,
  } = useSelector((state: RootState) => state.location);

  // ── Local submit flag — fixes the "always Submitting…" bug ──
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState<CreateAddressRequest>({
    fullName: "",
    email: "",
    phoneNumber: "",
    country: "India",
    state: "",
    district: "",
    city: "",
    addressLine1: "",
    addressLine2: "",
    landmark: "",
    pincode: "",
    addressType: "Home",
    defaultAddress: false,
    latitude: undefined,
    longitude: undefined,
  });

  // ── Fetch location data only when the user opens a country/state dropdown ──
  const ensureCountries = () => {
    if (countries.length === 0 && !locLoading) locDispatch(fetchAllCountries());
  };
  const ensureStatesForCountry = (countryName: string) => {
    ensureCountries();
    locDispatch(fetchStatesByCountryName(countryName));
  };

  // ── Success toast + reset ──
  useEffect(() => {
    if (success && message) {
      toast.success(message, { autoClose: 3000 });
      setIsSubmitting(false);
      resetForm();
      dispatch(clearAddressState());
    }
  }, [success, message]);

  // ── Error toast ──
  useEffect(() => {
    if (error) {
      toast.error(error, { autoClose: 3000 });
      setIsSubmitting(false);
    }
  }, [error]);

  const resetForm = () => {
    setFormData({
      fullName: "",
      email: "",
      phoneNumber: "",
      country: "India",
      state: "",
      district: "",
      city: "",
      addressLine1: "",
      addressLine2: "",
      landmark: "",
      pincode: "",
      addressType: "Home",
      defaultAddress: false,
    });
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value, type } = e.target;
    let val: string | boolean =
      type === "checkbox" ? (e.target as HTMLInputElement).checked : value;

    // Real-time validation
    if (typeof val === "string") {
      if (name === "fullName") {
        val = val
          .replace(/[^a-zA-Z\s]/g, "")
          .replace(/\s{2,}/g, " ")
          .slice(0, 40);
      } else if (name === "email") {
        // Only "@" and "." allowed once, max 30 chars, max 6 digits
        val = val.replace(/[^a-zA-Z0-9@.]/g, "").slice(0, 30);
        const atCount = (val.match(/@/g) || []).length;
        const dotCount = (val.match(/\./g) || []).length;
        if (atCount > 1 || dotCount > 1) return;
        const numericCount = val.replace(/\D/g, "").length;
        if (numericCount > 6) return;
      } else if (name === "city") {
        // Only alphabets and max 2 spaces, max 25 chars
        val = val.replace(/[^a-zA-Z\s]/g, "").replace(/\s{2,}/g, " ");
        const spaceCount = (val.match(/\s/g) || []).length;
        if (spaceCount > 2) return;
        val = val.slice(0, 25);
      } else if (name === "phoneNumber") {
        val = val.replace(/\D/g, "").slice(0, 10);
      } else if (name === "addressLine1" || name === "addressLine2") {
        val = val.replace(/[^a-zA-Z0-9\s\-\/\_]/g, "").slice(0, 100);
      } else if (name === "landmark") {
        val = val.replace(/[^a-zA-Z0-9\s]/g, "").replace(/^\s+/, "").replace(/\s{2,}/g, " ");
        // Strip digits beyond 2
        let digitCount = 0;
        val = val.replace(/\d/g, (d) => digitCount++ < 2 ? d : "");
        val = val.slice(0, 30);
      } else if (name === "pincode") {
        val = val.replace(/\D/g, "").slice(0, 6);
      }
    }

    setFormData((prev) => ({ ...prev, [name]: val }));
  };

  const handleCountrySelect = (countryName: string) => {
    setFormData((prev) => ({
      ...prev,
      country: countryName,
      state: "",
      district: "",
    }));
    locDispatch(fetchStatesByCountryName(countryName));
  };

  const handleStateSelect = (stateName: string) => {
    setFormData((prev) => ({ ...prev, state: stateName, district: "" }));
    locDispatch(fetchDistrictsByStateName(stateName));
  };

  const handleDistrictSelect = (districtName: string) => {
    setFormData((prev) => ({ ...prev, district: districtName }));
  };

  const handleLocationChange = (lat: number, lng: number) => {
    setFormData((prev) => ({ ...prev, latitude: lat, longitude: lng }));
  };

  // Auto-fill address fields from the pinned point (reverse geocoded).
  const handleAddressResolve = (addr: ResolvedAddress) => {
    setFormData((prev) => ({
      ...prev,
      country: addr.country || prev.country,
      city: addr.city || prev.city,
      state: addr.state || prev.state,
      district: addr.district || prev.district,
      pincode: addr.pincode || prev.pincode,
      addressLine1: prev.addressLine1 || addr.addressLine || "",
    }));
  if (addr.country) locDispatch(fetchStatesByCountryName(addr.country));
    if (addr.state) locDispatch(fetchDistrictsByStateName(addr.state));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Final Validations
    const atCount = (formData.email.match(/@/g) || []).length;
    const dotCount = (formData.email.match(/\./g) || []).length;
    if (atCount !== 1 || dotCount !== 1) {
      toast.error("Email must contain exactly one '@' and one '.' character.");
      return;
    }

    if (formData.phoneNumber.length !== 10) {
      toast.error("Mobile number must be exactly 10 digits.");
      return;
    }

    if (formData.pincode.length !== 6) {
      toast.error("Pincode must be exactly 6 digits.");
      return;
    }
    if (formData.pincode === "000000") {
      toast.error("Invalid pincode (000000).");
      return;
    }

    // State and district are custom dropdowns — browser required won't fire for them
    if (!formData.state) {
      toast.error("Please select a state.");
      return;
    }
    if (!formData.district) {
      toast.error("Please select a district.");
      return;
    }

    // Duplicate check (mirrors AddNewAddress)
    const isDuplicate = addresses.some(
      (addr) =>
        addr.fullName.trim().toLowerCase() ===
          formData.fullName.trim().toLowerCase() &&
        addr.addressLine1.trim().toLowerCase() ===
          formData.addressLine1.trim().toLowerCase() &&
        addr.city.trim().toLowerCase() === formData.city.trim().toLowerCase() &&
        addr.pincode.trim() === formData.pincode.trim(),
    );

    if (isDuplicate) {
      toast.error("This address already exists.");
      return;
    }

    if (formData.latitude == null || formData.longitude == null) {
      toast.error("Please pin your exact location on the map.");
      return;
    }

    // Drop empty optional fields — backend @IsOptional skips undefined/null but
    // still validates empty strings, which fails the addressLine2/landmark rules.
    const payload: CreateAddressRequest = { ...formData };
    if (!payload.addressLine2?.trim()) delete payload.addressLine2;
    if (!payload.landmark?.trim()) delete payload.landmark;

    setIsSubmitting(true);
    dispatch(addAddress(payload));
  };

  const countryOptions = countries.map((c) => c.countryName).sort((a, b) => a.localeCompare(b));
  const stateOptions = states.map((s) => s.stateName).sort((a, b) => a.localeCompare(b));
  const districtOptions = districts.map((d) => d.districtName).sort((a, b) => a.localeCompare(b));

  // ── Shared form body ─────────────────────────────────────────────────────────
  const formBody = (
    <form onSubmit={handleSubmit} className="p-0 border-radius-0 mb-4 bg-white">
      {/* Full Name */}
      <div className="d-flex justify-content-between align-items-start gap-3 mb-3 mt-2">
        <div className="w-100">
          <label className="form-label text-black fw-bold">Full Name</label>
          <div className="input-group input-group-outline input-group-lg">
            <input
              type="text"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              className="form-control"
              placeholder="Enter Full Name"
              required
            />
          </div>
        </div>
      </div>

      {/* Country + State (search dropdowns) */}
      <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
        <SearchDropdown
          label="Country"
          value={formData.country}
          options={countryOptions}
          onSelect={handleCountrySelect}
          onOpen={ensureCountries}
          loading={locLoading && countries.length === 0}
          placeholder="Search country..."
        />
        <SearchDropdown
          label="State"
          value={formData.state}
          options={stateOptions}
          onSelect={handleStateSelect}
          onOpen={() => ensureStatesForCountry(formData.country || "India")}
          disabled={!formData.country}
          loading={locLoading && !!formData.country && states.length === 0}
          placeholder="Search state..."
        />
      </div>

      {/* District (search dropdown) + City (plain text) */}
      <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
        <SearchDropdown
          label="District"
          value={formData.district}
          options={districtOptions}
          onSelect={handleDistrictSelect}
          disabled={!formData.state}
          loading={locLoading && !!formData.state && districts.length === 0}
          placeholder="Search district..."
        />
        <div className="w-100">
          <label className="form-label text-black fw-bold">City</label>
          <div className="input-group input-group-outline input-group-lg">
            <input
              type="text"
              name="city"
              className="form-control"
              placeholder="Enter City"
              value={formData.city}
              onChange={handleChange}
              required
            />
          </div>
        </div>
      </div>

      {/* Email + Mobile */}
      <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
        <div className="w-100">
          <label className="form-label text-black fw-bold">E-mail Id</label>
          <div className="input-group input-group-outline input-group-lg">
            <input
              type="email"
              name="email"
              className="form-control"
              placeholder="Enter e-mail Id"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>
        </div>
        <div className="w-100">
          <label className="form-label text-black fw-bold">Mobile Number</label>
          <div className="input-group input-group-outline input-group-lg">
            <input
              type="text"
              name="phoneNumber"
              className="form-control"
              placeholder="Enter mobile number"
              value={formData.phoneNumber}
              onChange={handleChange}
              required
              pattern="[0-9]{10}"
              title="Please enter a valid 10-digit phone number"
            />
          </div>
        </div>
      </div>

      {/* Address Line 1 + 2 */}
      <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
        <div className="w-100">
          <label className="form-label text-black fw-bold">
            Address Line 1
          </label>
          <div className="input-group input-group-outline input-group-lg">
            <input
              type="text"
              name="addressLine1"
              className="form-control"
              placeholder="Address Line 1"
              value={formData.addressLine1}
              onChange={handleChange}
              required
            />
          </div>
        </div>
        <div className="w-100">
          <label className="form-label text-black fw-bold">
            Address Line 2
          </label>
          <div className="input-group input-group-outline input-group-lg">
            <input
              type="text"
              name="addressLine2"
              className="form-control"
              placeholder="Address Line 2"
              value={formData.addressLine2}
              onChange={handleChange}
            />
          </div>
        </div>
      </div>

      {/* Landmark + Pincode */}
      <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
        <div className="w-100">
          <label className="form-label text-black fw-bold">Landmark</label>
          <div className="input-group input-group-outline input-group-lg">
            <input
              type="text"
              name="landmark"
              className="form-control"
              placeholder="Landmark (optional)"
              value={formData.landmark}
              onChange={handleChange}
            />
          </div>
        </div>
        <div className="w-100">
          <label className="form-label text-black fw-bold">Pincode</label>
          <div className="input-group input-group-outline input-group-lg">
            <input
              type="text"
              name="pincode"
              className="form-control"
              placeholder="Enter pincode"
              value={formData.pincode}
              onChange={handleChange}
              required
            />
          </div>
        </div>
      </div>

      {/* Address Type */}
      <div className="mb-3">
        <label className="form-label text-black fw-bold">Address Type</label>
        <div className="d-flex gap-3 mt-1">
          {["Home", "Office", "Other"].map((t) => (
            <div key={t} className="form-check">
              <input
                className="form-check-input"
                type="radio"
                name="addressType"
                id={`nav-type-${t}`}
                value={t}
                checked={formData.addressType === t}
                onChange={handleChange}
              />
              <label className="form-check-label" htmlFor={`nav-type-${t}`}>
                {t}
              </label>
            </div>
          ))}
        </div>
      </div>

      {/* Default checkbox */}
      <div className="form-check mb-3">
        <input
          className="form-check-input"
          type="checkbox"
          name="defaultAddress"
          checked={formData.defaultAddress}
          onChange={handleChange}
          id="nav-defaultCheck"
        />
        <label className="custom-control-label" htmlFor="nav-defaultCheck">
          Save this information for next time (Set as default)
        </label>
      </div>

      {/* Location pin */}
      <div className="mb-3">
        <LocationPicker
          latitude={formData.latitude}
          longitude={formData.longitude}
          onChange={handleLocationChange}
          onAddressResolve={handleAddressResolve}
        />
      </div>

      {/* Submit */}
      <div className="text-center mb-4 pt-4">
        <button
          type="submit"
          disabled={isSubmitting}
          className="btn btn-dark w-80 btn-md"
        >
          {isSubmitting ? "Submitting..." : "Submit"}
        </button>
      </div>
    </form>
  );

  return (
    <>
      {/* ── DESKTOP (display-max) ── */}
      <div className="display-max">{formBody}</div>

      {/* ── MOBILE (display-mini) ── */}
      <div className="display-mini display-mob px-2 pb-4">{formBody}</div>
    </>
  );
};

export default NewAddressForm;
